import {GA4EventName, GA4EventParamName, trackGA4CustomEvent} from '@Libraries/ga-events';
import {openPremiumUpsellModal} from '@Modals/premium-upsell-modal/premium-upsell-modal.library';

/**
 * Where a Teams upsell dialog was opened from. Each value is sent as the `source` of `Teams_UpsellDialog_Shown`
 * and stored as the upgrade attribution `teams_dialog_<value>`, so a value must never be renamed once shipped.
 */
export enum TeamsUpsellSource {
  DESIGN_SHARE_DIALOG = 'design_sharedialog',
  FOLDER_SHARE_DIALOG = 'folder_sharedialog',
  EDITOR_MORE_OPTIONS = 'editor_moreoptions',
  MY_STUFF_DESIGN_TOGGLE = 'mystuff_designtoggle',
  MY_STUFF_FOLDER_TOGGLE = 'mystuff_foldertoggle',
  MY_STUFF_SIDEBAR = 'mystuff_sidebar',
  MY_STUFF_SHARED_FILTER = 'mystuff_sharedfilter',
  MY_STUFF_CONTENT_FILTER = 'mystuff_contentfilter',
  CONTENT_PLANNER_FILTER = 'contentplanner_filter',
  MAILING_LISTS_FILTER = 'mailinglists_filter',
  SIGNUP_FORMS_FILTER = 'signupforms_filter',
  FORM_MAKER_LISTS_FILTER = 'formmaker_listsfilter',
  DESIGN_SELECTOR_SHARED_TAB = 'designselector_sharedtab',
  EVENT_PAGE_TOGGLE = 'eventpage_toggle',
  EVENTS_UPSELL = 'events_shareteam',
  SOCIAL_POST_TOGGLE = 'socialpost_toggle',
  SOCIAL_POST_GRID = 'socialpost_grid',
  SOCIAL_POST_MENU = 'socialpost_menu',
  SOCIAL_ACCOUNT_TOGGLE = 'socialaccount_toggle',
  SOCIAL_ACCOUNT_MENU = 'socialaccount_menu',
  EMAIL_TOGGLE = 'email_toggle',
  EMAIL_MAKER_POPUP = 'emailmaker_popup',
  EMAIL_CAMPAIGNS_PAGE = 'emailcampaigns_page',
  EMAIL_GRID_MENU = 'email_gridmenu',
  EMAILS_SHARED_TAB = 'emails_sharedtab',
  MAILING_LIST_TOGGLE = 'mailinglist_toggle',
  MAILING_LIST_FANCY_TOGGLE = 'mailinglist_fancytoggle',
  MAILING_LIST_MENU = 'mailinglist_menu',
  SIGNUP_FORM_TOGGLE = 'signupform_toggle',
  SIGNUP_FORM_PANEL = 'signupform_panel',
  SIGNUP_FORM_ROW = 'signupform_row',
  SIGNUP_FORM_MENU = 'signupform_menu',
  FORM_MAKER_ROW = 'formmaker_row',
}

export interface TeamsUpsellDialogOptions {
  onDialogLoaded?: () => void;
  shouldOpenInNewTab?: boolean;
}

const TEAMS_UPSELL_ATTRIBUTION_PREFIX = 'teams_dialog_';

/**
 * Opens the Teams premium upsell dialog and records which surface opened it, both as a GA4 "shown" event and as the
 * attribution source a subscription started from this dialog is credited to. The event fires on the open request, so a
 * dialog that then fails to load its content and closes itself is still counted.
 */
export const openTeamsUpsellDialog = (source: TeamsUpsellSource, {onDialogLoaded, shouldOpenInNewTab = false}: TeamsUpsellDialogOptions = {}): void => {
  trackGA4CustomEvent(GA4EventName.TEAMS_UPSELL_DIALOG_SHOWN, {[GA4EventParamName.SOURCE]: source});
  openPremiumUpsellModal(window.PMW.PREMIUM_ONLY_FEATURE_NAME_TEAMS, {
    onDialogLoaded,
    shouldOpenInNewTab,
    entryPoint: source,
    upsellDialogName: `${TEAMS_UPSELL_ATTRIBUTION_PREFIX}${source}`,
  });
};
